import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError, rupiah } from "@/lib/api";
import { PageHeader, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Wallet, PiggyBank, CreditCard, Loader2, AlertCircle, Lock, Receipt, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import { PaymentMethodDialog } from "@/components/PaymentMethodDialog";
import { PendingPaymentBanner } from "@/components/PendingPaymentBanner";

const today = new Date().toISOString().slice(0, 10);
const MIN_TOTAL = 10000;

export default function ParentBills() {
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [billsBy, setBillsBy] = useState({});
  const [sel, setSel] = useState({});
  const [billAmt, setBillAmt] = useState({});
  const [topup, setTopup] = useState({});
  const [savings, setSavings] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [wd, setWd] = useState(null); // student for withdrawal dialog
  const [wdAmt, setWdAmt] = useState("");
  const [wdNote, setWdNote] = useState("");
  const [wdBusy, setWdBusy] = useState(false);
  const [pendingPay, setPendingPay] = useState(null);

  const loadAll = async () => {
    const { data: studs } = await api.get("/parent/students");
    setStudents(studs);
    const entries = await Promise.all(
      studs.map(async (s) => {
        const { data } = await api.get(`/parent/students/${s.id}/bills`);
        return [s.id, data.filter((b) => b.status !== "paid")];
      })
    );
    setBillsBy(Object.fromEntries(entries));
    return entries;
  };

  useEffect(() => {
    (async () => {
      try {
        const entries = await loadAll();
        const allBills = entries.flatMap(([, bs]) => bs);
        const initSel = {};
        const initAmt = {};
        allBills.filter((b) => (b.due_date || "") < today).forEach((b) => {
          initSel[b.id] = true;
          const remaining = b.total_amount - b.paid_amount;
          const n = b.installment_allowed && b.installments > 1 ? b.installments : 1;
          const per = n > 1 ? Math.min(Math.round(b.total_amount / n), remaining) : remaining;
          const isLast = remaining <= per + 1;
          initAmt[b.id] = String(b.installment_allowed && n > 1 && !isLast ? per : remaining);
        });
        const st = location.state;
        if (st?.preselect) initSel[st.preselect] = true;
        setSel(initSel);
        setBillAmt(initAmt);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overdueOf = (sid) => (billsBy[sid] || []).filter((b) => (b.due_date || "") < today);
  const overdueUnselected = (sid) => overdueOf(sid).filter((b) => !sel[b.id]);
  const allOverdueUnselected = students.flatMap((s) => overdueUnselected(s.id));
  const hasOverdueUnselected = allOverdueUnselected.length > 0;

  const instInfo = (b) => {
    const remaining = b.total_amount - b.paid_amount;
    const n = b.installment_allowed && b.installments > 1 ? b.installments : 1;
    const per = n > 1 ? Math.min(Math.round(b.total_amount / n), remaining) : remaining;
    const isLast = remaining <= per + 1;
    const canInstallment = b.installment_allowed && n > 1 && !isLast;
    const paidCount = per > 0 ? Math.round(b.paid_amount / (b.total_amount / n)) : 0;
    return { remaining, n, per, isLast, canInstallment, currentNo: paidCount + 1 };
  };

  const toggle = (b) => {
    const isOverdue = (b.due_date || "") < today;
    if (!isOverdue && overdueUnselected(b.student_id).length > 0 && !sel[b.id]) {
      toast.error("Lunasi tunggakan yang sudah jatuh tempo terlebih dahulu.");
      return;
    }
    const turningOn = !sel[b.id];
    setSel((p) => ({ ...p, [b.id]: !p[b.id] }));
    if (turningOn) {
      const info = instInfo(b);
      setBillAmt((p) => ({ ...p, [b.id]: String(info.canInstallment ? info.per : info.remaining) }));
    }
  };

  const billPayAmount = (b) => {
    const info = instInfo(b);
    if (!info.canInstallment) return info.remaining;
    let amt = parseFloat(billAmt[b.id]);
    if (!amt || amt <= 0) amt = info.per;
    return Math.min(amt, info.remaining);
  };

  const items = [];
  Object.values(billsBy).flat().forEach((b) => {
    if (sel[b.id]) items.push({ type: "bill", student_id: b.student_id, bill_id: b.id, amount: billPayAmount(b) });
  });
  students.forEach((s) => {
    const tu = parseFloat(topup[s.id]);
    if (tu > 0) items.push({ type: "topup", student_id: s.id, amount: tu });
    const sv = parseFloat(savings[s.id]);
    if (sv > 0) items.push({ type: "savings", student_id: s.id, amount: sv });
  });
  const total = items.reduce((a, i) => a + i.amount, 0);
  const belowMin = items.length > 0 && total < MIN_TOTAL;
  const totalUnpaid = Object.values(billsBy).flat().reduce((a, b) => a + (b.total_amount - b.paid_amount), 0);

  const pay = () => {
    if (pendingPay) return toast.error("Selesaikan atau batalkan kode bayar sebelumnya dulu.");
    if (hasOverdueUnselected) return toast.error("Centang semua tagihan yang sudah lewat jatuh tempo terlebih dahulu.");
    if (items.length === 0) return toast.error("Pilih minimal satu item untuk dibayar");
    if (total < MIN_TOTAL) return toast.error(`Minimal total pembayaran ${rupiah(MIN_TOTAL)}`);
    setMethodOpen(true);
  };

  const confirmMethod = async (method, channel) => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/checkout-multi", {
        items, origin_url: window.location.origin, payment_method: method, payment_channel: channel,
      });
      setMethodOpen(false);
      if (data.redirect_url) window.location.href = data.redirect_url;
      else navigate(`/payment/instructions?session_id=${data.session_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const submitWithdraw = async () => {
    const amt = parseFloat(wdAmt);
    if (!amt || amt <= 0) return toast.error("Masukkan jumlah penarikan yang valid");
    if (amt > (wd.savings_balance || 0)) return toast.error("Jumlah melebihi saldo tabungan");
    setWdBusy(true);
    try {
      await api.post("/parent/withdrawals", { student_id: wd.id, amount: amt, note: wdNote });
      toast.success("Permintaan penarikan tabungan dikirim. Menunggu persetujuan admin.");
      setWd(null); setWdAmt(""); setWdNote("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setWdBusy(false); }
  };

  if (loading) return <p className="text-stone-400">Memuat…</p>;

  return (
    <div className="pb-6">
      <PageHeader title="Tagihan" subtitle="Centang tagihan yang ingin dibayar, tambah uang jajan/tabungan, lalu tekan Bayar Sekarang." />

      <PendingPaymentBanner onState={setPendingPay} />

      {students.length === 0 ? (
        <Empty>Belum ada santri terhubung.</Empty>
      ) : (
        <>
          {hasOverdueUnselected && (
            <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-4" data-testid="overdue-warning">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              Ada {allOverdueUnselected.length} tagihan yang sudah lewat jatuh tempo dan belum dicentang. Anda tidak bisa membayar apa pun sebelum semua tunggakan tersebut dicentang.
            </div>
          )}

          {/* 1. Tabel tagihan belum dibayar + isian saldo/tabungan per anak */}
          <div className="space-y-6">
            {students.map((s) => {
              const bills = billsBy[s.id] || [];
              const odUnsel = overdueUnselected(s.id);
              return (
                <div key={s.id} data-testid={`pay-student-${s.id}`} className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-heading font-bold text-white ${s.gender === "putra" ? "bg-blue-500" : "bg-pink-500"}`}>{s.name.charAt(0)}</div>
                    <div>
                      <p className="font-heading font-semibold text-stone-900">{s.name}</p>
                      <p className="text-xs text-stone-400 font-mono">NISN {s.nisn} · {s.gender}</p>
                    </div>
                  </div>

                  {odUnsel.length > 0 && (
                    <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      Ada {odUnsel.length} tunggakan jatuh tempo. Tagihan lain terkunci sampai tunggakan dipilih.
                    </div>
                  )}

                  <div className="space-y-2">
                    {bills.length === 0 ? (
                      <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">✓ Tidak ada tagihan tertunggak</p>
                    ) : (
                      bills.map((b) => {
                        const isOverdue = (b.due_date || "") < today;
                        const locked = !isOverdue && odUnsel.length > 0 && !sel[b.id];
                        const info = instInfo(b);
                        const remaining = info.remaining;
                        return (
                          <div key={b.id} data-testid={`pay-bill-${b.id}`}
                            className={`rounded-xl border px-3 py-2.5 transition-all ${sel[b.id] ? "border-emerald-400 bg-emerald-50" : locked ? "border-stone-200 bg-stone-50 opacity-60" : "border-stone-200 hover:border-emerald-300"}`}>
                            <div className={`flex items-center gap-3 ${locked ? "cursor-not-allowed" : "cursor-pointer"}`} onClick={() => !locked && toggle(b)}>
                              <Checkbox checked={!!sel[b.id]} onCheckedChange={() => toggle(b)} disabled={locked} data-testid={`pay-bill-check-${b.id}`} onClick={(e) => e.stopPropagation()} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-stone-800 text-sm">{b.label}</p>
                                  {isOverdue && <span className="text-[10px] font-bold uppercase tracking-wide text-rose-600 bg-rose-100 rounded-full px-2 py-0.5">Jatuh Tempo</span>}
                                  {b.installment_allowed && b.installments > 1 && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-100 rounded-full px-2 py-0.5">Cicilan {info.currentNo}/{info.n}</span>}
                                  <StatusBadge status={b.status} />
                                  {locked && <Lock size={12} className="text-stone-400" />}
                                </div>
                                <p className="text-xs text-stone-400 mt-0.5">Jatuh tempo {b.due_date} · Sisa {rupiah(remaining)}</p>
                              </div>
                              <span className="font-mono text-sm font-semibold text-stone-700">{rupiah(sel[b.id] ? billPayAmount(b) : remaining)}</span>
                            </div>
                            {sel[b.id] && info.canInstallment && (
                              <div className="mt-2.5 ml-7 rounded-lg bg-white border border-amber-200 p-2.5">
                                <label className="text-xs font-medium text-stone-600">Jumlah cicilan ke-{info.currentNo} dari {info.n} (Rp)</label>
                                <Input type="number" inputMode="numeric" data-testid={`pay-bill-amount-${b.id}`} value={billAmt[b.id] ?? ""} onChange={(e) => setBillAmt((p) => ({ ...p, [b.id]: e.target.value }))} className="rounded-lg mt-1 h-9" placeholder={String(info.per)} />
                                <p className="text-[11px] text-stone-400 mt-1">Saran cicilan {rupiah(info.per)} · Sisa setelah ini {rupiah(Math.max(0, remaining - billPayAmount(b)))}</p>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* isian tambah saldo & tabungan di bawah tabel */}
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div className="rounded-xl border border-stone-200 p-3">
                      <p className="text-xs font-medium text-stone-500 flex items-center gap-1.5 mb-1.5"><Wallet size={13} className="text-emerald-600" /> Top-up Uang Jajan</p>
                      <Input type="number" inputMode="numeric" data-testid={`pay-topup-${s.id}`} value={topup[s.id] || ""} onChange={(e) => setTopup((p) => ({ ...p, [s.id]: e.target.value }))} placeholder="0" className="rounded-lg" />
                    </div>
                    <div className="rounded-xl border border-stone-200 p-3">
                      <p className="text-xs font-medium text-stone-500 flex items-center gap-1.5 mb-1.5"><PiggyBank size={13} className="text-amber-500" /> Setor Tabungan</p>
                      <Input type="number" inputMode="numeric" data-testid={`pay-savings-${s.id}`} value={savings[s.id] || ""} onChange={(e) => setSavings((p) => ({ ...p, [s.id]: e.target.value }))} placeholder="0" className="rounded-lg" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 2. Total belum dibayar + Tombol Bayar Sekarang */}
          <div className="mt-6 bg-white border border-stone-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stone-500">Total belum dibayar</span>
              <span className="font-heading text-xl font-bold text-stone-900" data-testid="total-unpaid">{rupiah(totalUnpaid)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-stone-100 pt-3">
              <div>
                <p className="text-xs text-stone-400">{items.length} item dipilih untuk dibayar</p>
                <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="pay-total">{rupiah(total)}</p>
                {belowMin && <p className="text-xs text-rose-600">Minimal total {rupiah(MIN_TOTAL)}</p>}
                {pendingPay && <p className="text-xs text-amber-700" data-testid="pending-block-hint">Ada kode bayar aktif — selesaikan / batalkan dulu di banner atas.</p>}
              </div>
              <Button onClick={pay} disabled={processing || items.length === 0 || belowMin || hasOverdueUnselected || !!pendingPay} data-testid="pay-now-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl px-8 py-6 text-base font-semibold">
                {processing ? <Loader2 className="animate-spin" /> : (<><CreditCard size={18} className="mr-2" /> Bayar Sekarang</>)}
              </Button>
            </div>
          </div>

          {/* 3. Info saldo & tabungan anak (+ Tarik Tabungan) */}
          <h3 className="font-heading font-semibold text-stone-900 mt-8 mb-3">Uang Jajan & Tabungan Anak</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {students.map((s) => (
              <div key={s.id} data-testid={`balance-card-${s.id}`} className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm">
                <p className="font-heading font-semibold text-stone-900">{s.name}</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-stone-50 border p-3">
                    <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><Wallet size={12} /> Uang Jajan</p>
                    <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.personal_balance)}</p>
                  </div>
                  <div className="rounded-xl bg-stone-50 border p-3">
                    <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><PiggyBank size={12} /> Tabungan</p>
                    <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.savings_balance)}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setWd(s); setWdAmt(""); setWdNote(""); }} data-testid={`withdraw-btn-${s.id}`} className="w-full mt-3 rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50">
                  <ArrowUpFromLine size={15} className="mr-1.5" /> Tarik Tabungan
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <PaymentMethodDialog open={methodOpen} onOpenChange={setMethodOpen} total={total} confirming={processing} onConfirm={confirmMethod} />

      <Dialog open={!!wd} onOpenChange={(o) => !o && setWd(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Tarik Tabungan</DialogTitle></DialogHeader>
          {wd && (
            <div className="space-y-4">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm">
                <p className="font-medium text-stone-800">{wd.name}</p>
                <p className="text-stone-500">Saldo tabungan: <span className="font-mono font-semibold text-amber-700">{rupiah(wd.savings_balance)}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Jumlah Penarikan (Rp)</Label>
                <Input type="number" inputMode="numeric" data-testid="withdraw-amount" value={wdAmt} onChange={(e) => setWdAmt(e.target.value)} className="rounded-xl" placeholder="cth: 100000" />
              </div>
              <div className="space-y-2">
                <Label>Catatan (opsional)</Label>
                <Input data-testid="withdraw-note" value={wdNote} onChange={(e) => setWdNote(e.target.value)} className="rounded-xl" placeholder="cth: untuk keperluan libur" />
              </div>
              <p className="text-xs text-stone-400">Penarikan akan diproses setelah disetujui admin/bendahara pesantren.</p>
              <DialogFooter>
                <Button onClick={submitWithdraw} disabled={wdBusy} data-testid="withdraw-submit" className="bg-amber-500 hover:bg-amber-600 rounded-xl">
                  {wdBusy ? <Loader2 size={16} className="animate-spin" /> : "Ajukan Penarikan"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
